import React, { useEffect, useRef, useMemo, useState } from "react";
import ReactToPdf from "react-to-pdf";
import styles from "../../styles/AuditLogModal.module.css";
import previewStyles from "./PreviewModal.module.css";
import { Button } from "../../components/Modal";
import ReportPage from "../../components/Report/ReportPage";
import ReportGraph from "../../components/Report/ReportGraph";
import TransmissionsPage from "../../components/Report/TransmissionsPage";
import NotesPage from "../../components/Report/NotesPage";
import useFetchBillingCycle from "../../hooks/patients/useFetchBillingCyclePdf";
import useFetchTransmissionPdf from "../../hooks/patients/useFetchTransmissionPdf";
import useFetchPatientNotes from "../../hooks/patients/useFetchPatientNotes";
import useFetchRiskRating from "../../hooks/patients/useFetchRiskRating";
import useFetchAffect from "../../hooks/patients/useFetchAffect";
import useImpactOfLivingReport from "../../hooks/patients/useImpactOfLivingReport";
import useFetchSideEffects from "../../hooks/patients/useFetchSideEffects";
import useFetchOpioidReport from "../../hooks/patients/useFetchOpioidReport";
import { IPatient } from "../../hooks/patients/index.type";
import useFetchPegGraph from "../../hooks/patients/useFetchPegGraph";
import useFetchPdiGraph from "../../hooks/patients/useFetchPdiGraph";
import useFetchPhqGraph from "../../hooks/patients/useFetchPhqGraph";
import useFetchAnxietyGraph from "../../hooks/patients/useFetchAnxietyGraph";
import { useGetDates } from "../../hooks/patients";

function chunkArray<T>(arr: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

interface Props {
  show: boolean;
  onClose: () => void;
  patient: IPatient;
  selectedDate: { date: string; billingCycleId: string } | null;
  setSelectedDate: (params: { date: string; billingCycleId: string }) => void;
  notes?: {
    id: string;
    date: string;
    type: "Clinical" | "Non-Clinical";
    content: string;
    author: string;
    isImportant?: "yes" | "no";
  }[];
  initialCycle?: { billingCycleId: string; date: string };
}

const PreviewModalVersion2: React.FC<Props> = ({
  onClose,
  show,
  patient,
  notes: propNotes = [],
  selectedDate,
  setSelectedDate,
  initialCycle,
}) => {
  const transformedPropNotes = React.useMemo(() => {
    return propNotes.map((note) => ({
      _id: note.id,
      noteCategory: note.type.toLowerCase() as "clinical" | "non-clinical",
      richText: note.content,
      createdTimestamp: new Date(note.date).getTime(),
      isImportant: note.isImportant,
    }));
  }, [propNotes]);

  const [pdfDownloading, setPdfDownloading] = useState(false);

  // graph hooks
  const { pegGraph, getPegGraphData, pegGraphLoading } = useFetchPegGraph(
    selectedDate?.billingCycleId ?? ""
  );
  const { pdiGraph, getPdiGraphData, pdiGraphLoading } = useFetchPdiGraph(
    selectedDate?.billingCycleId ?? ""
  );
  const { phqGraph, getPhqGraphData, phqGraphLoading } = useFetchPhqGraph(
    selectedDate?.billingCycleId ?? ""
  );
  const { anxietyGraph, getAnxietyGraphData, anxietyGraphLoading } =
    useFetchAnxietyGraph(selectedDate?.billingCycleId ?? "");

  const { billingCycleData, getBillingCycleData, billingCycleLoading } =
    useFetchBillingCycle(selectedDate?.billingCycleId || "");

  const { datesData, getDates, datesLoading } = useGetDates({
    patientId: patient?._id,
  });

  // Auto-select latest billing cycle on first load
  useEffect(() => {
    if (datesData && datesData.length && !selectedDate) {
      const sortedDates = [...datesData].sort(
        (a, b) =>
          new Date(b.billingCycleStartDate).getTime() -
          new Date(a.billingCycleStartDate).getTime()
      );
      const latestDate = sortedDates[0];
      setSelectedDate({
        date: latestDate.billingCycleStartDate,
        billingCycleId: latestDate._id,
      });
    }
  }, [datesData, selectedDate]);

  useEffect(() => {
    if (initialCycle && show) {
      setSelectedDate(initialCycle);
      return;
    }
  }, []);

  const { riskRatingData, riskRatingLoading } = useFetchRiskRating(
    selectedDate?.billingCycleId || ""
  );

  const { affectData, isLoading: isAffectDataLoading } = useFetchAffect(
    selectedDate?.billingCycleId || ""
  );

  const {
    impactData: impactOfLivingData,
    isLoading: isImpactDataLoading,
    refetch: refetchImpact,
  } = useImpactOfLivingReport(selectedDate?.billingCycleId || "");
  const { sideEffects, isLoading: isSideEffectsLoading } = useFetchSideEffects(
    selectedDate?.billingCycleId || ""
  );

  const { opioidData, isLoading: isOpioidDataLoading } = useFetchOpioidReport(
    selectedDate?.billingCycleId || ""
  );

  const {
    notes,
    loading: notesLoading,
    patientBillingMinuteMessage,
  } = useFetchPatientNotes(selectedDate?.billingCycleId);

  const sortedNotes = useMemo(() => {
    if (!notes) return [];
    return [...notes].sort((a, b) => {
      const order = {
        clinical: 1,
        "non-clinical": 2,
        "timer-summary": 3,
      };

      // sort by priority
      if (order[a.noteCategory] !== order[b.noteCategory]) {
        return order[a.noteCategory] - order[b.noteCategory];
      }
      // If same category -> sort by date
      return a.createdTimestamp - b.createdTimestamp;
    });
  }, [notes]);

  const reportRef = useRef<HTMLDivElement>(null);
  const {
    transmissionData,
    fetchTransmissionData,
    isLoading: transmissionLoading,
    error: transmissionError,
  } = useFetchTransmissionPdf(selectedDate?.billingCycleId || "");

  const allDataLoaded = !(
    billingCycleLoading ||
    transmissionLoading ||
    notesLoading ||
    riskRatingLoading ||
    isAffectDataLoading ||
    isImpactDataLoading ||
    isSideEffectsLoading ||
    isOpioidDataLoading ||
    pegGraphLoading ||
    pdiGraphLoading ||
    phqGraphLoading ||
    anxietyGraphLoading
  );
  const chunkedQuestions = useMemo(() => {
    if (!transmissionData?.data) return [];

    const firstPageQuestions = transmissionData.data.slice(0, 11);
    const remainingQuestions = transmissionData.data.slice(11);
    const remainingChunks = chunkArray(remainingQuestions, 10);

    return [firstPageQuestions, ...remainingChunks];
  }, [transmissionData?.data]);

  const generatePdf = async () => {
    if (!allDataLoaded) {
      console.log("Waiting for all data to load...");
      return;
    }

    if (reportRef.current && billingCycleData) {
      setPdfDownloading(true);
      const formatPatientName = (fullName?: string) => {
        if (!fullName?.trim()) return "Patient";
        const nameParts = fullName.trim().split(/\s+/);
        if (nameParts.length === 1) {
          return nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        }
        const firstName = nameParts[0];
        const lastName = nameParts[nameParts.length - 1];
        return `${firstName.charAt(0).toUpperCase()}${lastName
          .charAt(0)
          .toUpperCase()}${lastName.slice(1).toLowerCase()}`;
      };

      const formatBillingDate = (dateString?: string) => {
        const date = dateString ? new Date(dateString) : new Date();
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const month = months[date.getMonth()];
        const year = date.getFullYear().toString().slice(-2);
        return `${month}${year}`;
      };

      const patientName = formatPatientName(billingCycleData?.patientId?.name);
      const billingMonthYear = formatBillingDate(
        billingCycleData?.billingCycleStartDate
      );

      const filename = `${patientName}_RTMReport_${billingMonthYear}.pdf`;

      await ReactToPdf(() => reportRef.current as HTMLElement, {
        filename,
        page: {
          orientation: "portrait",
          format: "letter",
          margin: { top: 8, right: 8, bottom: 8, left: 8 },
        },
      });

      setPdfDownloading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (show) {
      getDates();
    }
  }, [show]);

  // Billing cycle specific data (only when cycle changes)
  useEffect(() => {
    if (show && selectedDate?.billingCycleId) {
      getPegGraphData();
      getPdiGraphData();
      getPhqGraphData();
      getAnxietyGraphData();
      getBillingCycleData();
      fetchTransmissionData();
    }
  }, [show, selectedDate?.billingCycleId]);

  const graphs = [
    {
      title: "PEG Score",
      data: pegGraph?.data,
      loading: pegGraphLoading,
      color: "rgb(75, 192, 192)",
    },
    {
      title: "PDI Score",
      data: pdiGraph?.data,
      loading: pdiGraphLoading,
      color: "rgb(0, 148, 194)",
    },
    {
      title: "PHQ-2 Score",
      data: phqGraph?.data,
      loading: phqGraphLoading,
      color: "rgb(1, 194, 255)",
    },
    {
      title: "Anxiety Score",
      data: anxietyGraph?.data,
      loading: anxietyGraphLoading,
      color: "rgb(15, 64, 122)",
    },
  ];

  if (!show) return null;

  return (
    <>
      <div className={styles.overlay}>
        <div className={styles.centerContainer}>
          {/* LEFT SECTION */}
          <div className={styles.sidebarContainer}>
            <div style={{ marginBottom: "1rem" }}>
              <h2 className={styles.largeTitle}>Billing Cycles</h2>
              <div className={styles.smallTitle}>
                Select a cycle to view details:
              </div>
              {datesLoading ? (
                <div className={styles.loadingDates}>Loading dates...</div>
              ) : (
                <ul className={styles.customList}>
                  {datesData && datesData.length ? (
                    [...datesData]
                      .sort(
                        (a, b) =>
                          new Date(b.billingCycleStartDate).getTime() -
                          new Date(a.billingCycleStartDate).getTime()
                      )
                      .map((date) => {
                        const isSelected =
                          selectedDate?.date === date.billingCycleStartDate;
                        return (
                          <li
                            key={date._id}
                            onClick={() =>
                              setSelectedDate({
                                date: date.billingCycleStartDate,
                                billingCycleId: date._id,
                              })
                            }
                            style={{
                              backgroundColor: isSelected
                                ? "#DBEAFE"
                                : "transparent",
                              borderLeft: isSelected
                                ? "4px solid #0F407A"
                                : "4px solid transparent",
                            }}
                            className={styles.menuItem}
                          >
                            <span className={styles.dot}></span>
                            {date.billingCycleStartDate}
                          </li>
                        );
                      })
                  ) : (
                    <div style={{ fontSize: "1.4rem", fontWeight: 400 }}>
                      No data available
                    </div>
                  )}
                </ul>
              )}
            </div>
          </div>
          <div className={styles.modal2}>
            <div className={styles.header2}>
              <h2>Physician’s Report</h2>
              <div className={styles.header_actions}>
                <Button
                  span={false}
                  label={
                    pdfDownloading
                      ? "Downloading PDF..."
                      : allDataLoaded
                      ? "Download PDF"
                      : "Loading Data..."
                  }
                  icon="icon-file_download"
                  onClick={generatePdf}
                  disabled={!allDataLoaded || pdfDownloading}
                  submitButtonStyle={{
                    borderRadius: "0.6rem",
                    display: "flex",
                    flexDirection: "row-reverse",
                  }}
                />
                <button onClick={onClose} className={styles.closeButton}>
                  <svg className={styles.closeButtonIcon}>
                    <use xlinkHref="/assets/sprite.svg#icon-close-cross" />
                  </svg>
                </button>
              </div>
            </div>

            {/* PREVIEW CONTENT */}
            <div className={styles.modal_content}>
              <ReportPage
                patientId={patient?._id || ""}
                billingCycleData={billingCycleData}
                sideEffectQuestions={[]}
                billingCycleDataLoading={billingCycleLoading}
                sideEffectQuestionsLoading={false}
                isReadyForDownload={true}
                customHeight="11.3in"
                billingCycleId={selectedDate?.billingCycleId}
                refetchImpact={refetchImpact}
                impactOfLivingData={impactOfLivingData}
                isImpactDataLoading={isImpactDataLoading}
                isAffectDataLoading={isAffectDataLoading}
                affectData={affectData}
                isSideEffectsLoading={isSideEffectsLoading}
                sideEffects={sideEffects}
                isOpioidDataLoading={isOpioidDataLoading}
                opioidData={opioidData}
                riskRatingLoading={riskRatingLoading}
                riskRatingData={riskRatingData}
              />

              <ReportGraph
                billingCycleData={billingCycleData}
                sideEffectQuestions={[]}
                graphs={graphs}
                customHeight="11.3in"
              />

              {chunkedQuestions.map((chunk, idx) => {
                const startingIndex = idx === 0 ? 0 : 11 + (idx - 1) * 10;
                return (
                  <div
                    key={idx}
                    style={{ pageBreakAfter: "always", marginBottom: "20px" }}
                  >
                    <TransmissionsPage
                      patientId={patient?._id}
                      customHeight="11.2in"
                      transmissionData={{ data: chunk }}
                      isLoading={transmissionLoading}
                      error={transmissionError}
                      startingIndex={startingIndex}
                    />
                  </div>
                );
              })}
              {sortedNotes.length && (
                <div style={{ marginBottom: "20px" }}>
                  <NotesPage
                    notes={sortedNotes}
                    loading={notesLoading}
                    customHeight="11.2in"
                    isPdf={false}
                    patientBillingMinuteMessage={patientBillingMinuteMessage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className={previewStyles.pdfGenerator}
        style={{ position: "absolute", left: "-9999px" }}
      >
        <div ref={reportRef}>
          <ReportPage
            patientId={patient?._id || ""}
            billingCycleData={billingCycleData}
            sideEffectQuestions={[]}
            billingCycleDataLoading={billingCycleLoading}
            sideEffectQuestionsLoading={false}
            isReadyForDownload={allDataLoaded}
            customHeight="11.2in"
            billingCycleId={selectedDate?.billingCycleId}
            refetchImpact={refetchImpact}
            impactOfLivingData={impactOfLivingData}
            isImpactDataLoading={isImpactDataLoading}
            isAffectDataLoading={isAffectDataLoading}
            affectData={affectData}
            isSideEffectsLoading={isSideEffectsLoading}
            sideEffects={sideEffects}
            isOpioidDataLoading={isOpioidDataLoading}
            opioidData={opioidData}
            riskRatingLoading={riskRatingLoading}
            riskRatingData={riskRatingData}
          />

          <ReportGraph
            billingCycleData={billingCycleData}
            sideEffectQuestions={[]}
            graphs={graphs}
            customHeight="11.2in"
          />
          {chunkedQuestions.map((chunk, idx) => {
            const startingIndex = idx === 0 ? 0 : 11 + (idx - 1) * 10;
            return (
              <div key={idx} style={{ pageBreakAfter: "always" }}>
                <TransmissionsPage
                  patientId={patient?._id}
                  customHeight="11.2in"
                  transmissionData={{ data: chunk }}
                  isLoading={transmissionLoading}
                  error={null}
                  startingIndex={startingIndex}
                />
              </div>
            );
          })}

          {sortedNotes.length && (
            <div style={{ marginBottom: "20px" }}>
              <NotesPage
                notes={sortedNotes}
                loading={notesLoading}
                customHeight="11.2in"
                isPdf={true}
                patientBillingMinuteMessage={patientBillingMinuteMessage}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PreviewModalVersion2;
